/**
 * PatientVitalsTrends — longitudinal vitals view.
 *
 * For a given patient, renders:
 *   - Date range selector (30 / 90 / 180 / 365 days)
 *   - Bucket size selector (day / week / month)
 *   - Vital-sign chip selector (which lines to show)
 *   - Recharts line chart per visible vital, with normal-range bands
 *   - AI clinical assessment panel (loads on demand)
 *
 * Talks to:
 *   db:vitals:trends                  — time-bucketed aggregates
 *   db:vitals:assess-health-status    — Bedrock AI assessment
 *
 * Permission: vitals:read for both data and AI assessment. No PHI in
 * URL params or React state beyond what's necessary for charting.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, ReferenceArea,
  Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { Activity, TrendingUp, TrendingDown, Minus, AlertCircle, Loader2, Sparkles } from "lucide-react";

const DEFAULT_VISIBLE = [
  "blood_pressure_sys", "blood_pressure_dia", "heart_rate", "oxygen_saturation", "temperature_f",
];

const SEVERITY_BADGE = {
  info: { bg: "#e8f0fb", border: "#c9d6e8", color: "#1f6feb", label: "Informational" },
  attention: { bg: "#fef6e0", border: "#e0b973", color: "#7c5210", label: "Attention" },
  concern: { bg: "#fde8e8", border: "#f5a5a5", color: "#b00020", label: "Concern" },
  urgent: { bg: "#fcdcdc", border: "#d24747", color: "#7a0014", label: "Urgent" },
};

const TREND_ICON = {
  improving: { I: TrendingUp, color: "#2d6b2d", label: "improving" },
  stable: { I: Minus, color: "#5a6478", label: "stable" },
  concerning: { I: AlertCircle, color: "#7c5210", label: "drifting" },
  worsening: { I: TrendingDown, color: "#b00020", label: "worsening" },
  insufficient_data: { I: Minus, color: "#9aa4b2", label: "not enough data" },
  no_data: { I: Minus, color: "#9aa4b2", label: "no data" },
};

export default function PatientVitalsTrends({ patientId, user }) {
  const [windowDays, setWindowDays] = useState(90);
  const [bucket, setBucket] = useState("day");
  const [visibleVitals, setVisibleVitals] = useState(DEFAULT_VISIBLE);

  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [assessment, setAssessment] = useState(null);
  const [assessLoading, setAssessLoading] = useState(false);
  const [assessError, setAssessError] = useState(null);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await window.zignagenixAPI.invoke("db:vitals:trends", {
        patientId, windowDays, bucket,
      });
      if (r && r.error) {
        setError(r.message || r.code || "Failed to load trends");
        setTrends(null);
      } else {
        setTrends(r);
      }
    } catch (e) {
      setError((e && e.message) || "Failed to load trends");
    }
    setLoading(false);
  }, [patientId, windowDays, bucket]);

  useEffect(() => { load(); }, [load]);

  async function runAssessment() {
    setAssessLoading(true);
    setAssessError(null);
    setAssessment(null);
    try {
      const r = await window.zignagenixAPI.invoke("db:vitals:assess-health-status", {
        patientId, windowDays,
      });
      if (r && r.error) setAssessError(r.message || "Assessment failed");
      else setAssessment(r);
    } catch (e) {
      setAssessError((e && e.message) || "Assessment failed");
    }
    setAssessLoading(false);
  }

  const orderedVitals = useMemo(() => {
    if (!trends || !trends.trends) return [];
    return Object.entries(trends.trends).filter(([, t]) => t.overall && t.overall.count > 0);
  }, [trends]);

  if (!patientId) {
    return <div style={{ padding: 12, color: "#9aa4b2" }}>Select a patient to view vitals trends.</div>;
  }

  return (
    <div style={{ padding: "12px 16px", background: "#fff" }}>

      {/* ── Header + controls ─────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Activity size={18} color="#1f6feb" />
          <h2 style={{ margin: 0, fontSize: 16 }}>Vitals trends</h2>
          {trends && (
            <span style={{ fontSize: 11, color: "#5a6478" }}>
              {trends.total_observations} observation{trends.total_observations !== 1 ? "s" : ""} in last {windowDays} days
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <RangeChip label="30d" active={windowDays === 30} onClick={() => setWindowDays(30)} />
          <RangeChip label="90d" active={windowDays === 90} onClick={() => setWindowDays(90)} />
          <RangeChip label="180d" active={windowDays === 180} onClick={() => setWindowDays(180)} />
          <RangeChip label="1y" active={windowDays === 365} onClick={() => setWindowDays(365)} />
          <span style={{ width: 10 }} />
          <RangeChip label="day" active={bucket === "day"} onClick={() => setBucket("day")} />
          <RangeChip label="wk" active={bucket === "week"} onClick={() => setBucket("week")} />
          <RangeChip label="mo" active={bucket === "month"} onClick={() => setBucket("month")} />
        </div>
      </div>

      {error && (
        <div style={alertStyle("error")}><AlertCircle size={14} /> {error}</div>
      )}

      {/* ── AI assessment panel ────────────────────────────────── */}
      <div style={{ border: "1px solid #c9d6e8", borderRadius: 4, padding: 12, marginBottom: 14, background: "#f4f7fb" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Sparkles size={14} color="#1f6feb" />
            <strong style={{ fontSize: 13 }}>Clinical AI assessment</strong>
            <span style={{ fontSize: 11, color: "#5a6478" }}>
              Bedrock Claude, BAA-internal. Surfaces concerns — never diagnoses.
            </span>
          </div>
          <button onClick={runAssessment} disabled={assessLoading || loading || !trends} style={btnPrimary}>
            {assessLoading ? <><Loader2 size={12} className="spin" /> Assessing…</> : "Run assessment"}
          </button>
        </div>
        {assessError && (
          <div style={{ ...alertStyle("error"), marginTop: 8 }}>{assessError}</div>
        )}
        {assessment && assessment.assessment && (
          <div style={{ marginTop: 10 }}>
            <div style={{ marginBottom: 8 }}>
              <SeverityBadge severity={assessment.assessment.overall_severity} />
              <span style={{ fontSize: 11, color: "#5a6478", marginLeft: 8 }}>
                {assessment.assessment.concerns_count} concern{assessment.assessment.concerns_count !== 1 ? "s" : ""}
                {assessment.assessment.ai_unavailable ? " · structured analysis only (AI unavailable)" : ""}
              </span>
            </div>
            <div style={{
              fontSize: 12, lineHeight: 1.5, color: "#1f2a3b",
              whiteSpace: "pre-wrap", background: "#fff", padding: 10, borderRadius: 3, border: "1px solid #e1e6ed",
            }}>
              {assessment.assessment.summary_markdown}
            </div>
          </div>
        )}
      </div>

      {/* ── Vital chips selector ──────────────────────────────── */}
      {trends && orderedVitals.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
          {orderedVitals.map(([k, t]) => (
            <VitalChip
              key={k}
              active={visibleVitals.includes(k)}
              onClick={() => setVisibleVitals((prev) =>
                prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]
              )}
              label={t.label}
              trend={t.trend.direction}
            />
          ))}
        </div>
      )}

      {/* ── Charts ─────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ color: "#9aa4b2", padding: 24, textAlign: "center" }}><Loader2 className="spin" /> Loading trends…</div>
      ) : !trends || orderedVitals.length === 0 ? (
        <div style={{ color: "#9aa4b2", padding: 24, textAlign: "center", fontStyle: "italic" }}>
          No vital signs recorded in this window.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {orderedVitals
            .filter(([k]) => visibleVitals.includes(k))
            .map(([k, t]) => (
              <VitalChart key={k} vitalKey={k} data={t} />
            ))}
        </div>
      )}
    </div>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────

function RangeChip({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: "4px 10px", fontSize: 11, borderRadius: 12,
      background: active ? "#1f6feb" : "#fff",
      color: active ? "#fff" : "#5a6478",
      border: `1px solid ${active ? "#1f6feb" : "#c9d6e8"}`,
      cursor: "pointer",
    }}>{label}</button>
  );
}

function VitalChip({ label, active, onClick, trend }) {
  const ti = TREND_ICON[trend] || TREND_ICON.stable;
  const I = ti.I;
  return (
    <button onClick={onClick} style={{
      padding: "4px 10px", fontSize: 12, borderRadius: 14,
      background: active ? "#e8f0fb" : "#fff",
      color: active ? "#1f6feb" : "#5a6478",
      border: `1px solid ${active ? "#1f6feb" : "#c9d6e8"}`,
      cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5,
    }}>
      <I size={11} color={ti.color} />
      {label}
    </button>
  );
}

function SeverityBadge({ severity }) {
  const s = SEVERITY_BADGE[severity] || SEVERITY_BADGE.info;
  return (
    <span style={{
      display: "inline-block",
      padding: "3px 10px", fontSize: 11, fontWeight: 700,
      borderRadius: 12,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      textTransform: "uppercase", letterSpacing: 0.4,
    }}>{s.label}</span>
  );
}

function VitalChart({ vitalKey, data }) {
  const points = (data.points || []).map((p) => ({
    bucket: shortDate(p.bucket),
    mean: p.mean, min: p.min, max: p.max,
  }));
  const ti = TREND_ICON[data.trend.direction] || TREND_ICON.stable;
  const TI = ti.I;

  return (
    <div style={{ border: "1px solid #e1e6ed", borderRadius: 4, padding: 10, background: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <strong style={{ fontSize: 12 }}>{data.label}</strong>
        <span style={{ fontSize: 11, color: ti.color, display: "inline-flex", alignItems: "center", gap: 4 }}>
          <TI size={11} /> {ti.label}
        </span>
      </div>
      <div style={{ fontSize: 11, color: "#5a6478", marginBottom: 6 }}>
        latest <strong>{data.overall.latest}{data.unit}</strong> · mean {data.overall.mean} · range {data.overall.min}-{data.overall.max} ·{" "}
        {data.abnormal_count} abnormal of {data.overall.count}
      </div>
      <ResponsiveContainer width="100%" height={140}>
        <LineChart data={points} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f3f7" />
          <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: "#5a6478" }} />
          <YAxis tick={{ fontSize: 10, fill: "#5a6478" }} domain={['auto', 'auto']} />
          {data.normal_low != null && data.normal_high != null && (
            <ReferenceArea y1={data.normal_low} y2={data.normal_high} fill="#e8f5e9" fillOpacity={0.4} />
          )}
          <Tooltip contentStyle={{ fontSize: 11 }} />
          <Line type="monotone" dataKey="mean" stroke="#1f6feb" strokeWidth={2} dot={{ r: 2 }} />
          <Line type="monotone" dataKey="min" stroke="#9aa4b2" strokeWidth={1} dot={false} strokeDasharray="3 3" />
          <Line type="monotone" dataKey="max" stroke="#9aa4b2" strokeWidth={1} dot={false} strokeDasharray="3 3" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function shortDate(iso) {
  const d = new Date(iso);
  if (isNaN(d.valueOf())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const btnPrimary = {
  padding: "5px 10px", fontSize: 12, fontWeight: 600,
  background: "#1f6feb", color: "#fff", border: "none", borderRadius: 3, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 4,
};
function alertStyle(kind) {
  return {
    padding: "6px 10px", borderRadius: 3, fontSize: 12,
    display: "flex", alignItems: "center", gap: 6,
    background: kind === "ok" ? "#e9f5e9" : "#fde8e8",
    border: kind === "ok" ? "1px solid #a5d5a5" : "1px solid #f5a5a5",
    color: kind === "ok" ? "#2d6b2d" : "#8b0000",
  };
}
